import React, { useEffect, useMemo, useState } from 'react';
import { BaseModal, Button, ToastMessage } from '@cokitchen/cokitchen-components';
import { ReactComponent as RiderIcon } from 'assets/icons/rider.svg';
import { ReactComponent as NigeriaFlag } from 'assets/icons/nigeria-flag.svg';
import { ReactComponent as CancelIcon } from 'assets/icons/cancel-small.svg';
import { ReactComponent as ZoomIcon } from 'assets/icons/zooom.svg';
import { apiService } from 'utils/api';
import { toast } from 'react-toastify';
import { ExternalRiderType } from 'models/external-rider.model';
import RejectExternalRiderModal from '../reject-external-rider-modal';
import { getRiderDetails } from './utils';
import { STORAGE_KEYS } from 'utils/constants';
import { CoKitchenType } from 'models/cokitchen.model';
import { ENUM_RIDER_STATUS } from 'utils/enum';
import ScheduledRiderModal from '../schedule-rider-modal';

type Props = {
  onClose: () => void;
  onSuccess: () => void;
  open: boolean;
  rider: ExternalRiderType;
};

type FooterProps = {
  onCancel: () => void;
  loading: boolean;
  onConfirm: () => void;
  disableBtn: boolean;
  rider: ExternalRiderType;
};

const ExternalRiderDetailsFooter = ({
  onCancel,
  onConfirm,
  loading,
  disableBtn,
  rider
}: FooterProps) => (
  <div className='grid grid-cols-2 gap-x-4'>
    <Button
      disabled={disableBtn}
      small
      color='primary'
      variant='outline'
      className='mx-auto'
      onClick={onCancel}>
      Reject
    </Button>
    {rider.status !== ENUM_RIDER_STATUS.AWAITING_APPROVAL && (
      <Button
        small
        disabled={disableBtn}
        color='primary'
        variant='block'
        className='mx-auto'
        loading={loading}
        onClick={onConfirm}>
        Approve
      </Button>
    )}
    {rider.status === ENUM_RIDER_STATUS.AWAITING_APPROVAL && (
      <Button
        small
        disabled={disableBtn}
        color='transparent'
        variant='block'
        className='mx-auto !bg-blue-5 text-white'
        onClick={onConfirm}>
        Schedule
      </Button>
    )}
  </div>
);
const ExternalRiderDetailsModal = ({ onClose, open, rider, onSuccess }: Props) => {
  const [previewDetails, setPreviewDetails] = useState({ name: '', previewLink: '' });
  const [loading, setLoading] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [openRejectRiderModal, setOpenRejectRiderModal] = useState(false);
  const [externalRiderDetails, setExternalRiderDetails] = useState<ExternalRiderType>(rider);

  const [openScheduledRiderModal, setOpenScheduledRiderModal] = useState(false);

  const handleGetExternalRider = async () => {
    setLoadingDetails(true);
    try {
      const res = await apiService.get(`rider-applications/${rider.application_id}`, {
        baseUrl: process.env.REACT_APP_AUTH_API_URL
      });

      if (res?.data) {
        setExternalRiderDetails(res.data);
      }
    } finally {
      setLoadingDetails(false);
    }
  };
  useEffect(() => {
    handleGetExternalRider();
  }, []);

  const cokitchenList = localStorage.getItem(STORAGE_KEYS.COKITCHEN_LIST);
  const cokitchenArray: CoKitchenType[] =
    cokitchenList === 'undefined' ? [] : JSON.parse(cokitchenList || '[]');

  const cokitchenDetails = cokitchenArray.find(
    (cokitchen) => cokitchen.id === externalRiderDetails?.cokitchen_id
  );

  const riderActivePolygon = cokitchenDetails?.cokitchen_polygons.filter((polygon) =>
    externalRiderDetails.cokitchen_polygon_ids.includes(polygon.id)
  );

  const riderDetails = useMemo(
    () =>
      getRiderDetails({
        ...externalRiderDetails,
        cokitchenName: cokitchenDetails?.name ?? 'N/A',
        riderPolygon: riderActivePolygon ?? []
      }),
    [externalRiderDetails]
  );

  const handleApproveRider = async () => {
    setLoading(true);
    const url = `rider-applications/approve/${rider.application_id}`;

    try {
      await apiService.patch(url, {
        baseUrl: process.env.REACT_APP_AUTH_API_URL
      });
      toast(<ToastMessage type='success' message={'Rider approved successfully'} />);
      onClose();
    } catch (err: any) {
      toast(<ToastMessage type='error' message={'An error occured'} />);
    } finally {
      setLoading(false);
    }
  };

  return (
    <BaseModal
      isOpen={open}
      onRequestClose={onClose}
      position='right'
      title='Rider Details'
      // bodyClassName={'pt-5'}
      titleIcon={<RiderIcon />}
      footer={
        rider.status !== ENUM_RIDER_STATUS.REJECTED ? (
          <ExternalRiderDetailsFooter
            rider={rider}
            loading={loading}
            disableBtn={loadingDetails}
            onCancel={() => setOpenRejectRiderModal(true)}
            onConfirm={() =>
              rider.status === ENUM_RIDER_STATUS.AWAITING_APPROVAL
                ? setOpenScheduledRiderModal(true)
                : handleApproveRider()
            }
          />
        ) : undefined
      }>
      <div>
        {externalRiderDetails?.selfie?.url && (
          <span>
            <img
              src={externalRiderDetails.selfie.url}
              alt="rider's selfie"
              className='w-20 h-20 rounded-full object-cover'
            />
          </span>
        )}
        <ul className='space-y-6 mt-8'>
          {riderDetails.map((detail) => (
            <li className='flex flex-col gap-1' key={detail.name}>
              <span className='text-sm font-medium  text-gray-6/80'>{detail.name}</span>
              <div className='rounded-md border border-gray-6/60 min-h-11 py-1 px-4 flex items-center justify-between'>
                <span className='flex items-center gap-3'>
                  {detail?.type === 'phone' && (
                    <span className='border-r pr-3.5'>
                      <NigeriaFlag className='size-4' />
                    </span>
                  )}
                  {detail.desc ?? 'N/A'}
                </span>
                {detail?.type === 'preview' && (
                  <button
                    onClick={() =>
                      setPreviewDetails({
                        name: detail.name,
                        previewLink: detail.url ?? ''
                      })
                    }
                    className='text-xs font-medium text-red-4 underline underline-offset-1'>
                    Preview
                  </button>
                )}
              </div>
              {previewDetails.previewLink && previewDetails.name === detail.name && (
                <div className='mt-2 relative'>
                  <img
                    src={previewDetails.previewLink}
                    alt='Bike dp'
                    className='w-full h-52 object-cover border-dashed'
                  />
                  <span className='flex items-center gap-3 top-2 right-2 absolute'>
                    <button
                      aria-label='Zoom icon'
                      onClick={() => window.open(previewDetails.previewLink, '_blank')}>
                      <ZoomIcon className='size-5' />
                    </button>
                    <button
                      aria-label='cancel icon'
                      onClick={() => setPreviewDetails({ name: '', previewLink: '' })}>
                      <CancelIcon className='size-5' />
                    </button>
                  </span>
                </div>
              )}
            </li>
          ))}
        </ul>
      </div>
      {openRejectRiderModal && (
        <RejectExternalRiderModal
          show={openRejectRiderModal}
          onClose={onClose}
          riders={[rider]}
          onSuccess={onSuccess}
        />
      )}
      {openScheduledRiderModal && (
        <ScheduledRiderModal
          onSuccess={onSuccess}
          riders={[rider]}
          closeModal={() => {
            setOpenScheduledRiderModal(false);
            onClose();
          }}
        />
      )}
    </BaseModal>
  );
};

export default ExternalRiderDetailsModal;
